export interface AmountInputFormat {
  decimals: number;
  decimalSeparator: string;
  groupSeparator: string;
}

export interface MaskedAmount {
  display: string;
  value: string;
}

const EMPTY: MaskedAmount = { display: "", value: "" };

/** The runtime's `numeric(20,8)` integer capacity. */
const MAX_INTEGER_DIGITS = 12;

export function amountInputFormat(locale: string, decimals: number): AmountInputFormat {
  const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
  const decimalSeparator = parts.find((part) => part.type === "decimal")?.value ?? ".";
  const groupSeparator =
    parts.find((part) => part.type === "group")?.value ?? (decimalSeparator === "," ? "." : ",");
  return { decimals, decimalSeparator, groupSeparator };
}

function digitsOf(text: string): string {
  return text.replace(/\D/g, "");
}

function normalizeInteger(digits: string): string {
  return digits.replace(/^0+(?=\d)/, "").slice(0, MAX_INTEGER_DIGITS);
}

function groupInteger(digits: string, separator: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

function looksGrouped(segments: string[]): boolean {
  const [head, ...rest] = segments;
  return rest.length > 0 && head.length >= 1 && head.length <= 3 && rest.every((part) => part.length === 3);
}

/** Decimal point index, or -1 when every separator reads as grouping: es "3.400" → 3400 but "30.5" → 30,5. */
function decimalIndex(text: string, decimalSeparator: string): number {
  const lastDot = text.lastIndexOf(".");
  const lastComma = text.lastIndexOf(",");
  if (lastDot === -1 && lastComma === -1) return -1;

  if (lastDot !== -1 && lastComma !== -1) {
    const last = Math.max(lastDot, lastComma);
    return text.indexOf(text[last]) === last ? last : -1;
  }

  const separator = lastDot !== -1 ? "." : ",";
  const segments = text.split(separator);
  if (separator === decimalSeparator) {
    return segments.length > 2 && looksGrouped(segments) ? -1 : text.indexOf(separator);
  }
  return looksGrouped(segments) ? -1 : text.lastIndexOf(separator);
}

function countOf(text: string, char: string): number {
  return char ? text.split(char).length - 1 : 0;
}

function isOneCharacterEdit(before: string, after: string): boolean {
  if (Math.abs(before.length - after.length) !== 1) return false;
  const [longer, shorter] = before.length > after.length ? [before, after] : [after, before];
  let index = 0;
  while (index < shorter.length && shorter[index] === longer[index]) index += 1;
  return longer.slice(index + 1) === shorter.slice(index);
}

/** Idempotent on its own `display`, so a re-render never changes the amount. */
// On a one-key edit, a group separator already in `previous` is grouping, never a decimal point.
export function maskAmountInput(raw: string, format: AmountInputFormat, previous?: string): MaskedAmount {
  let text = raw.replace(/[^\d.,]/g, "");
  const group = format.groupSeparator;
  if (
    previous !== undefined &&
    isOneCharacterEdit(previous, raw) &&
    group !== format.decimalSeparator &&
    countOf(previous, group) > 0 &&
    countOf(text, group) <= countOf(previous, group)
  ) {
    text = text.split(group).join("");
  }

  if (format.decimals <= 0) {
    const integer = normalizeInteger(digitsOf(text));
    return integer ? { display: groupInteger(integer, format.groupSeparator), value: integer } : EMPTY;
  }

  const index = decimalIndex(text, format.decimalSeparator);
  const hasPoint = index !== -1;
  const fraction = hasPoint ? digitsOf(text.slice(index + 1)).slice(0, format.decimals) : "";
  const integer = normalizeInteger(digitsOf(hasPoint ? text.slice(0, index) : text)) || (hasPoint ? "0" : "");
  if (!integer) return EMPTY;

  const grouped = groupInteger(integer, format.groupSeparator);
  return {
    display: hasPoint ? `${grouped}${format.decimalSeparator}${fraction}` : grouped,
    value: fraction ? `${integer}.${fraction}` : integer,
  };
}

export function digitsBefore(text: string, position: number): number {
  return digitsOf(text.slice(0, Math.max(0, position))).length;
}

/** Caret index right after the `count`-th digit, so an edit mid-number survives the regrouping. */
export function caretAfterDigits(text: string, count: number): number {
  if (count <= 0) return 0;
  let seen = 0;
  for (let index = 0; index < text.length; index += 1) {
    if (text[index] >= "0" && text[index] <= "9") seen += 1;
    if (seen === count) return index + 1;
  }
  return text.length;
}
